import * as XLSX from 'xlsx';

const ASIN_PATTERN = /^[A-Z0-9]{10}$/i;

const HEADER_ALIASES = {
  asin: [
    'asin',
    'asin no',
    'asin number',
    'asins',
    '(parent) asin',
    'parent asin',
    'parent asin no',
    'parent asins',
    'child asin',
    '(child) asin',
  ],
  itemPrice: [
    'item price',
    'itemprice',
    'price',
    'selling price',
    'sale price',
    'mrp',
    'amount',
  ],
  shippingCharge: ['shipping charge', 'shipping', 'ship charge', 'buyer shipping'],
  fbmFulfilmentCost: [
    'fulfilment cost',
    'fulfilment',
    'fbm fulfilment cost',
    'fbm cost',
    'shipping cost',
  ],
  costOfGoods: [
    'cost of goods',
    'cost of goods sold',
    'cost of good',
    'cost of the product',
    'cost of product',
    'product cost',
    'cogs',
    'buy price',
    'purchase price',
    'landed cost',
  ],
};

function normalizeHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

/** Strip trailing notes like "@05%" so "cost of the product @05%" matches. */
function normalizeHeaderLoose(value) {
  return normalizeHeader(value)
    .replace(/@\s*\d+(\.\d+)?\s*%?/g, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function mapHeaderKeys(headers) {
  const mapping = {};
  for (const header of headers) {
    const normalized = normalizeHeader(header);
    const loose = normalizeHeaderLoose(header);

    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(normalized) || aliases.includes(loose)) {
        mapping[field] = header;
      }
    }

    // Extra COGS detection for seller sheet names like "cost of the product @05%"
    if (
      !mapping.costOfGoods &&
      (/cost of (the )?product/.test(loose) ||
        /cost of goods/.test(loose) ||
        /^cogs\b/.test(loose))
    ) {
      mapping.costOfGoods = header;
    }
  }
  return mapping;
}

function cellNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const cleaned = String(value).replace(/[^0-9.-]/g, '');
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

export function downloadBulkTemplate() {
  const sheet = XLSX.utils.aoa_to_sheet([
    ['(Parent) ASIN', 'SKU', 'cost of the product @05%'],
    ['B0EXAMPLE01', 'TIS-ASIN', 518.96],
    ['B0EXAMPLE02', 'TIS-ASIN', 472.9],
  ]);
  sheet['!cols'] = [{ wch: 16 }, { wch: 14 }, { wch: 24 }];
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'ASINs');
  XLSX.writeFile(book, 'fbm-bulk-asin-template.xlsx');
}

export function parseBulkAsinWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          reject(new Error('Excel file has no sheets.'));
          return;
        }

        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          defval: '',
          raw: true,
        });

        if (!rows.length) {
          reject(new Error('Excel sheet is empty.'));
          return;
        }

        const mapping = mapHeaderKeys(Object.keys(rows[0] || {}));
        if (!mapping.asin) {
          reject(
            new Error(
              'Could not find an ASIN column. Use "ASIN" or "(Parent) ASIN" (see template).'
            )
          );
          return;
        }

        const parsed = [];
        const seen = new Set();

        for (let index = 0; index < rows.length; index += 1) {
          const row = rows[index];
          const asinRaw = String(row[mapping.asin] || '').trim().toUpperCase();
          if (!asinRaw) continue;

          if (!ASIN_PATTERN.test(asinRaw)) {
            parsed.push({
              asin: asinRaw,
              invalid: true,
              error: `Row ${index + 2}: invalid ASIN "${asinRaw}"`,
            });
            continue;
          }

          if (seen.has(asinRaw)) {
            continue;
          }
          seen.add(asinRaw);

          parsed.push({
            asin: asinRaw,
            itemPrice: mapping.itemPrice ? cellNumber(row[mapping.itemPrice]) : null,
            shippingCharge: mapping.shippingCharge
              ? cellNumber(row[mapping.shippingCharge])
              : null,
            fbmFulfilmentCost: mapping.fbmFulfilmentCost
              ? cellNumber(row[mapping.fbmFulfilmentCost])
              : null,
            costOfGoods: mapping.costOfGoods ? cellNumber(row[mapping.costOfGoods]) : null,
          });
        }

        if (!parsed.length) {
          reject(new Error('No ASIN rows found in the Excel file.'));
          return;
        }

        resolve(parsed);
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Failed to read Excel file.'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file.'));
    reader.readAsArrayBuffer(file);
  });
}

export function downloadBulkResultsWorkbook(results, marketplace = 'IN') {
  const currency = marketplace === 'US' ? 'USD' : 'INR';
  const header = [
    'Row',
    'ASIN',
    'Status',
    'Title',
    'Brand',
    'Marketplace',
    'Currency',
    'Item Price',
    'Shipping Charge',
    'Sales Price',
    'Referral Fee',
    'Closing Fee',
    'Amazon Fees Total',
    'Other Fees & Taxes',
    'Fulfilment Cost',
    'Cost of Goods',
    'Total Costs',
    'Estimated Proceeds',
    'Error',
  ];

  const body = (results || []).map((row) => [
    row.rowNumber ?? '',
    row.asin || '',
    row.ok ? 'OK' : 'ERROR',
    row.title || '',
    row.brand || '',
    row.marketplace || marketplace,
    row.currency || currency,
    row.ok ? row.itemPrice : '',
    row.ok ? row.shippingCharge : '',
    row.ok ? row.salesPrice : '',
    row.ok ? row.referralFee : '',
    row.ok ? row.closingFee : '',
    row.ok ? row.amazonFeesTotal : '',
    row.ok ? row.otherFeesAndTaxes : '',
    row.ok ? row.fbmFulfilmentCost : '',
    row.ok ? row.costOfGoods : '',
    row.ok ? row.totalCosts : '',
    row.ok ? row.estimatedProceeds : '',
    row.ok ? '' : row.error || 'Failed',
  ]);

  const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
  sheet['!cols'] = header.map((name) => ({
    wch: Math.min(28, Math.max(12, name.length + 2)),
  }));

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Fee results');
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(book, `fbm-bulk-fees-${marketplace}-${stamp}.xlsx`);
}
